import React, { useState, useEffect } from 'react';
import SectionContainer from '../../components/UI/SectionContainer';
import Spinner from '../../components/UI/Spinner';
import Modal from '../../components/Layout/Modal';
import Pagination from '../../components/UI/Pagination';
import { openAlert, openConfirm, dateFormat } from '../../utils/common';
import mobileCodePolicyService from '../../services/mobileCodePolicyService';
import { sanitizeInput, validateNumericInput } from '../../utils/xssProtection';

const MobileCodePolicyPage = () => {
    const [policies, setPolicies] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [allChecked, setAllChecked] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create'); // 'create', 'edit'
    const [selectedPolicy, setSelectedPolicy] = useState(null);
    const [formData, setFormData] = useState({
        library_name: '',
        library_version: '',
        security_level: 'MEDIUM',
        is_allowed: true,
        allowed_versions: '',
        integrity_hash: '',
        source_url: '',
        description: '',
        blocking_reason: '',
        policy_rules: ''
    });
    const [validationErrors, setValidationErrors] = useState({});
    const [statistics, setStatistics] = useState(null);

    // 페이징 상태
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const pageSize = 10;
    const pageBlock = 5;

    // 필터 상태 (입력용)
    const [filters, setFilters] = useState({
        library_name: '',
        security_level: '',
        is_allowed: ''
    });

    // 실제 검색에 사용되는 필터 상태
    const [searchFilters, setSearchFilters] = useState({
        library_name: '',
        security_level: '',
        is_allowed: ''
    });

    useEffect(() => {
        loadPolicies();
        loadStatistics();
    }, [currentPage, searchFilters]);

    const loadPolicies = async (page = currentPage) => {
        setLoading(true);
        try {
            const response = await mobileCodePolicyService.getPolicies(searchFilters, page, pageSize);
            
            if (response.success) {
                setPolicies(response.data.policies || []);
                setTotalCount(response.data.totalCount || 0);
            } else {
                openAlert({ title: '오류', description: response.message });
            }
        } catch (error) {
            console.error('정책 목록 로드 오류:', error);
            openAlert({ title: '오류', description: '정책 목록을 불러오는데 실패했습니다.' });
        } finally {
            setLoading(false);
        }
    };

    const loadStatistics = async () => {
        try {
            const response = await mobileCodePolicyService.getPolicyStatistics();
            
            if (response.success) {
                setStatistics(response.data || {});
            }
        } catch (error) {
            console.error('통계 로드 오류:', error);
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        const sanitizedValue = sanitizeInput(value);
        setFilters(prev => ({
            ...prev,
            [name]: sanitizedValue
        }));
    };

    const handleSelectChange = (e) => {
        const { name, value } = e.target;
        const newFilters = {
            ...filters,
            [name]: value
        };
        setFilters(newFilters);
        setSearchFilters(newFilters); // 셀렉트 박스는 즉시 검색 실행
        setCurrentPage(1);
    };

    const handleSearch = () => {
        setCurrentPage(1);
        setSearchFilters(filters); // 입력된 필터를 검색 필터로 복사하여 검색 실행
    };

    const handleReset = () => {
        const resetFilters = {
            library_name: '',
            security_level: '',
            is_allowed: ''
        };
        setFilters(resetFilters);
        setSearchFilters(resetFilters);
        setCurrentPage(1);
    };

    const handleCheckboxChange = (id, checked) => {
        if (checked) {
            setSelectedIds(prev => [...prev, id]);
        } else {
            setSelectedIds(prev => prev.filter(selectedId => selectedId !== id));
        }
    };

    const handleAllCheckboxChange = (checked) => {
        setAllChecked(checked);
        if (checked) {
            setSelectedIds(policies.map(policy => policy.idx));
        } else {
            setSelectedIds([]);
        }
    };

    const openCreateModal = () => {
        setModalMode('create');
        setFormData({
            library_name: '',
            library_version: '',
            security_level: 'MEDIUM',
            is_allowed: true,
            allowed_versions: '',
            integrity_hash: '',
            source_url: '',
            description: '',
            blocking_reason: '',
            policy_rules: ''
        });
        setShowModal(true);
    };

    const openEditModal = (policy) => {
        setModalMode('edit');
        setSelectedPolicy(policy);
        setFormData({
            library_name: policy.libraryName || '',
            library_version: policy.libraryVersion || '',
            security_level: policy.securityLevel || 'MEDIUM',
            is_allowed: policy.isAllowed || false,
            allowed_versions: Array.isArray(policy.allowedVersions) ? policy.allowedVersions.join(', ') : (policy.allowedVersions || ''),
            integrity_hash: policy.integrityHash || '',
            source_url: policy.sourceUrl || '',
            description: policy.description || '',
            blocking_reason: policy.blockingReason || '',
            policy_rules: typeof policy.policyRules === 'object' ? JSON.stringify(policy.policyRules, null, 2) : (policy.policyRules || '')
        });
        setShowModal(true);
    };

    const handleFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        let sanitizedValue = value;
        let validationError = null;
        
        if (type !== 'checkbox') {
            // textarea 필드는 줄바꿈을 보존하기 위해 기본 sanitizeInput 대신 제한적 정화 적용
            if (e.target.tagName.toLowerCase() === 'textarea') {
                // textarea는 HTML 태그만 제거하고 줄바꿈은 보존
                sanitizedValue = value
                    .replace(/<[^>]*>/g, '') // HTML 태그 제거
                    .replace(/javascript:/gi, '') // 자바스크립트 프로토콜 제거
                    .replace(/on\w+\s*=/gi, ''); // 이벤트 핸들러 제거
            } else {
                sanitizedValue = sanitizeInput(value);
            }
            
            // 필드별 검증
            if (name === 'display_priority') {
                const numValue = parseInt(sanitizedValue, 10);
                const validation = validateNumericInput(numValue, 1, 10);
                validationError = validation.isValid ? null : validation.error;
            } else if (name === 'library_name' && sanitizedValue.length > 100) {
                validationError = '라이브러리명은 100자를 초과할 수 없습니다.';
            } else if (name === 'source_url' && sanitizedValue && !isValidUrl(sanitizedValue)) {
                validationError = '올바른 URL 형식을 입력해주세요.';
            }
        }
        
        setValidationErrors(prev => ({
            ...prev,
            [name]: validationError
        }));
        
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : sanitizedValue
        }));
    };
    
    const isValidUrl = (string) => {
        try {
            new URL(string);
            return true;
        } catch (_) {
            return false;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // 유효성 검사
        const hasErrors = Object.values(validationErrors).some(error => error !== null);
        if (hasErrors) {
            openAlert({ 
                title: '입력 오류', 
                description: '입력값을 확인해주세요.',
                type: 'error'
            });
            return;
        }
        
        if (!formData.library_name.trim() || !formData.library_version.trim()) {
            openAlert({ 
                title: '입력 오류', 
                description: '라이브러리명과 버전은 필수 입력 항목입니다.',
                type: 'error'
            });
            return;
        }
        
        try {
            // JSON 파싱 검증
            let policyRules = {};
            if (formData.policy_rules) {
                try {
                    policyRules = JSON.parse(formData.policy_rules);
                } catch (jsonError) {
                    openAlert({ 
                        title: '입력 오류', 
                        description: '정책 규칙의 JSON 형식이 올바르지 않습니다. 올바른 JSON 형식으로 입력해주세요.',
                        type: 'error'
                    });
                    return;
                }
            }

            const submitData = {
                ...formData,
                allowed_versions: formData.allowed_versions ? formData.allowed_versions.split(',').map(v => v.trim()) : [],
                policy_rules: policyRules
            };

            let response;
            if (modalMode === 'create') {
                response = await mobileCodePolicyService.createPolicy(submitData);
            } else {
                response = await mobileCodePolicyService.updatePolicy(selectedPolicy.idx, submitData);
            }

            if (response.success) {
                openAlert({ title: '성공', description: response.message });
                setShowModal(false);
                loadPolicies();
                loadStatistics();
            } else {
                openAlert({ title: '오류', description: response.message });
            }
        } catch (error) {
            console.error('정책 저장 오류:', error);
            openAlert({ title: '오류', description: '정책 저장 중 오류가 발생했습니다.' });
        }
    };

    const handleDelete = (id) => {
        openConfirm({
            title: '삭제하기',
            description: '선택한 정책을 삭제하시겠습니까?',
            positive_method: handleDeleteConfirm,
            positive_param: id,
            negative_method: null,
            negative_param: null,
            positive_btn: '확인',
            negative_btn: '취소',
        });
    };

    const handleDeleteConfirm = async (id) => {
        try {
            const response = await mobileCodePolicyService.deletePolicy(id);
            if (response.success) {
                openAlert({ title: '성공', description: response.message });
                loadPolicies();
                loadStatistics();
            } else {
                openAlert({ title: '오류', description: response.message });
            }
        } catch (error) {
            console.error('정책 삭제 오류:', error);
            openAlert({ title: '오류', description: '정책 삭제 중 오류가 발생했습니다.' });
        }
    };

    const handleBulkDelete = () => {
        if (selectedIds.length === 0) {
            openAlert({ 
                title: '선택 오류', 
                description: '삭제할 정책을 선택해주세요.',
                type: 'warning'
            });
            return;
        }

        openConfirm({
            title: '일괄 삭제',
            description: `선택한 ${selectedIds.length}개의 정책을 삭제하시겠습니까?\n삭제된 정책은 복구할 수 없습니다.`,
            positive_method: handleBulkDeleteConfirm,
            positive_param: null,
            negative_method: null,
            negative_param: null,
            positive_btn: '삭제',
            negative_btn: '취소',
        });
    };

    const handleBulkDeleteConfirm = async () => {
        try {
            const response = await mobileCodePolicyService.bulkDeletePolicies(selectedIds);
            if (response.success) {
                openAlert({ 
                    title: '성공', 
                    description: `${selectedIds.length}개의 정책이 성공적으로 삭제되었습니다.`
                });
                setSelectedIds([]);
                setAllChecked(false);
                loadPolicies();
                loadStatistics();
            } else {
                openAlert({ title: '오류', description: response.message });
            }
        } catch (error) {
            console.error('일괄 삭제 오류:', error);
            openAlert({ title: '오류', description: '정책 일괄 삭제 중 오류가 발생했습니다.' });
        }
    };

    const getSecurityLevelBadge = (level) => {
        const badges = {
            'LOW': 'badge-success',
            'MEDIUM': 'badge-warning', 
            'HIGH': 'badge-danger',
            'CRITICAL': 'badge-dark'
        };
        return badges[level] || 'badge-secondary';
    };

    const getSecurityLevelText = (level) => {
        const texts = {
            'LOW': '낮음',
            'MEDIUM': '보통',
            'HIGH': '높음', 
            'CRITICAL': '매우높음'
        };
        return texts[level] || level;
    };

    return (
      <SectionContainer title="모바일 코드 정책 관리" className="list">
        <Spinner visible={loading} />

        {/* 통계 카드 */}
        {statistics && (
          <div className="row mb-4">
            <div className="col-md-3">
              <div className="card text-center">
                <div className="card-body">
                  <h5 className="card-title text-primary">
                    {statistics.overview.total || 0}
                  </h5>
                  <p className="card-text">전체 정책</p>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card text-center">
                <div className="card-body">
                  <h5 className="card-title text-success">
                    {statistics.overview.allowed || 0}
                  </h5>
                  <p className="card-text">허용된 정책</p>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card text-center">
                <div className="card-body">
                  <h5 className="card-title text-danger">
                    {statistics.overview.blocked || 0}
                  </h5>
                  <p className="card-text">차단된 정책</p>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card text-center">
                <div className="card-body">
                  <h5 className="card-title text-warning">
                    {statistics.securityLevels.CRITICAL || 0}
                  </h5>
                  <p className="card-text">위험 정책</p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="search_area">
          <div className="acc_total">
            <h4>
              <b>{totalCount}</b>개 검색되었습니다.
            </h4>
          </div>
          <div className="left">
            <div className="select_box">
              <select
                name="security_level"
                value={filters.security_level}
                onChange={handleSelectChange}
              >
                <option value="">보안 레벨 전체</option>
                <option value="LOW">낮음</option>
                <option value="MEDIUM">보통</option>
                <option value="HIGH">높음</option>
                <option value="CRITICAL">매우높음</option>
              </select>
            </div>
            <div className="select_box" style={{ marginLeft: "10px" }}>
              <select
                name="is_allowed"
                value={filters.is_allowed}
                onChange={handleSelectChange}
              >
                <option value="">허용 상태 전체</option>
                <option value="1">허용</option>
                <option value="0">차단</option>
              </select>
            </div>
            <div className="search_wrap insert-wrap">
              <div className="search">
                <div className="input">
                  <input
                    type="text"
                    name="library_name"
                    value={filters.library_name}
                    onChange={handleFilterChange}
                    onKeyUp={(e) => e.key === "Enter" && handleSearch()}
                    placeholder="라이브러리명 검색"
                    maxLength="100"
                  />
                </div>
                <i onClick={handleSearch}>
                  <svg
                    width="25"
                    height="26"
                    viewBox="0 0 25 26"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M24.6582 21.6162L19.79 16.748C19.5703 16.5283 19.2725 16.4062 18.96 16.4062H18.1641C19.5117 14.6826 20.3125 12.5146 20.3125 10.1562C20.3125 4.5459 15.7666 0 10.1562 0C4.5459 0 0 4.5459 0 10.1562C0 15.7666 4.5459 20.3125 10.1562 20.3125C12.5146 20.3125 14.6826 19.5117 16.4062 18.1641V18.96C16.4062 19.2725 16.5283 19.5703 16.748 19.79L21.6162 24.6582C22.0752 25.1172 22.8174 25.1172 23.2715 24.6582L24.6533 23.2764C25.1123 22.8174 25.1123 22.0752 24.6582 21.6162ZM10.1562 16.4062C6.7041 16.4062 3.90625 13.6133 3.90625 10.1562C3.90625 6.7041 6.69922 3.90625 10.1562 3.90625C13.6084 3.90625 16.4062 6.69922 16.4062 10.1562C16.4062 13.6084 13.6133 16.4062 10.1562 16.4062Z"
                      fill="#45A3FB"
                    ></path>
                  </svg>
                </i>
              </div>
            </div>
            <div className="insert-wrap">
              <button className="btn btn-md primary mr-1" onClick={handleReset}>
                초기화
              </button>
            </div>
            <div className="but-flex">
              <button
                className="btn btn-md primary mr-1"
                onClick={openCreateModal}
              >
                정책 등록
              </button>
              <button
                className="btn btn-md danger"
                onClick={handleBulkDelete}
                disabled={selectedIds.length === 0}
                style={{
                  opacity: selectedIds.length === 0 ? 0.5 : 1,
                  cursor: selectedIds.length === 0 ? "not-allowed" : "pointer",
                }}
              >
                선택 삭제 ({selectedIds.length})
              </button>
            </div>
          </div>
        </div>

        <div className="content_area">
          <div className="table_area">
            <div className="table">
              <table>
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        checked={allChecked}
                        onChange={(e) =>
                          handleAllCheckboxChange(e.target.checked)
                        }
                      />
                    </th>
                    <th>No</th>
                    <th>라이브러리명</th>
                    <th>버전</th>
                    <th>보안 레벨</th>
                    <th>허용 상태</th>
                    <th>설명</th>
                    <th>차단 사유</th>
                    <th>등록일</th>
                    <th>관리</th>
                  </tr>
                </thead>
                <tbody>
                  {policies.length > 0 ? (
                    policies.map((policy, idx) => (
                      <tr key={policy.idx}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(policy.idx)}
                            onChange={(e) =>
                              handleCheckboxChange(policy.idx, e.target.checked)
                            }
                          />
                        </td>
                        <td>{(currentPage - 1) * pageSize + idx + 1}</td>
                        <td>
                          <strong>{policy.libraryName}</strong>
                        </td>
                        <td>{policy.libraryVersion}</td>
                        <td>
                          <span
                            className={getSecurityLevelBadge(
                              policy.securityLevel
                            )}
                          >
                            {getSecurityLevelText(policy.securityLevel)}
                          </span>
                        </td>
                        <td>
                          <span
                            className={policy.isAllowed ? "allowed" : "blocked"}
                          >
                            {policy.isAllowed ? "허용" : "차단"}
                          </span>
                        </td>
                        <td>{policy.description || "-"}</td>
                        <td>
                          {policy.isAllowed ? (
                            <span style={{ color: "#888" }}>-</span>
                          ) : (
                            <span
                              style={{
                                color: "#d32f2f",
                                fontSize: "0.9em",
                                maxWidth: "200px",
                                display: "inline-block",
                                wordBreak: "break-word",
                              }}
                            >
                              {policy.blockingReason ||
                                "차단된 라이브러리입니다."}
                            </span>
                          )}
                        </td>
                        <td>{dateFormat(policy.createdAt)}</td>
                        <td>
                          <div style={{ display: "flex", gap: "8px" }}>
                            <input
                              type="button"
                              className="btn fourbtn"
                              onClick={() => openEditModal(policy)}
                              value="수정하기"
                            />
                            <input
                              type="button"
                              className="btn fourbtn btn-danger"
                              onClick={() => handleDelete(policy.idx)}
                              value="삭제하기"
                            />
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="9"
                        style={{ textAlign: "center", padding: "50px" }}
                      >
                        등록된 정책이 없습니다.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {totalCount > 0 && (
          <Pagination
            currentPage={currentPage}
            totalCount={totalCount}
            pageSize={pageSize}
            pageBlock={pageBlock}
            onPageChange={handlePageChange}
          />
        )}

        {/* 정책 등록/수정 모달 */}
        <Modal
          title={modalMode === "create" ? "정책 등록" : "정책 수정"}
          className="terminal-info mt-2"
          modalClassName="modal-xl"
          confirmLabel={modalMode === "create" ? "등록" : "수정"}
          visible={showModal}
          onConfirm={handleSubmit}
          onClose={() => setShowModal(false)}
        >
          <div
            style={{ maxHeight: "70vh", overflowY: "auto", padding: "10px" }}
          >
            {/* 기본 정보 - 2열 레이아웃 */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "0px",
                marginBottom: "0px",
              }}
            >
              <dl>
                <dt>라이브러리명 *</dt>
                <dd>
                  <input
                    type="text"
                    name="library_name"
                    value={formData.library_name}
                    onChange={handleFormChange}
                    required
                    placeholder="예: chart.js"
                    style={{
                      width: "100%",
                      border: validationErrors.library_name
                        ? "2px solid #ff4444"
                        : "1px solid #ddd",
                    }}
                    maxLength="100"
                  />
                  {validationErrors.library_name && (
                    <div
                      style={{
                        color: "#ff4444",
                        fontSize: "12px",
                        marginTop: "5px",
                      }}
                    >
                      {validationErrors.library_name}
                    </div>
                  )}
                </dd>
              </dl>
              <dl>
                <dt>라이브러리 버전 *</dt>
                <dd>
                  <input
                    type="text"
                    name="library_version"
                    value={formData.library_version}
                    onChange={handleFormChange}
                    required
                    placeholder="예: 4.4.9"
                    maxLength="50"
                    style={{ width: "100%" }}
                  />
                </dd>
              </dl>
              <dl>
                <dt>보안 레벨 *</dt>
                <dd>
                  <select
                    name="security_level"
                    value={formData.security_level}
                    onChange={handleFormChange}
                    required
                    style={{ width: "100%" }}
                  >
                    <option value="LOW">낮음</option>
                    <option value="MEDIUM">보통</option>
                    <option value="HIGH">높음</option>
                    <option value="CRITICAL">매우높음</option>
                  </select>
                </dd>
              </dl>
              <dl>
                <dt>허용 상태</dt>
                <dd>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <input
                      type="checkbox"
                      name="is_allowed"
                      checked={formData.is_allowed}
                      onChange={handleFormChange}
                    />
                    허용
                  </label>
                </dd>
              </dl>
            </div>

            {/* 버전 및 URL 정보 - 2열 레이아웃 */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "0px",
                marginBottom: "20px",
              }}
            >
              <dl>
                <dt>허용된 버전 목록</dt>
                <dd>
                  <input
                    type="text"
                    name="allowed_versions"
                    value={formData.allowed_versions}
                    onChange={handleFormChange}
                    placeholder="쉼표로 구분하여 입력 (예: 4.4.9, 4.4.8)"
                    maxLength="500"
                    style={{ width: "100%" }}
                  />
                </dd>
              </dl>
              <dl>
                <dt>무결성 해시 (SHA-256)</dt>
                <dd>
                  <input
                    type="text"
                    name="integrity_hash"
                    value={formData.integrity_hash}
                    onChange={handleFormChange}
                    placeholder="선택사항: 라이브러리 파일의 SHA-256 해시값"
                    maxLength="512"
                    style={{ width: "100%" }}
                  />
                </dd>
              </dl>
            </div>

            {/* 소스 URL - 전체 너비 */}
            <dl>
              <dt>소스 URL</dt>
              <dd>
                <input
                  type="url"
                  name="source_url"
                  value={formData.source_url}
                  onChange={handleFormChange}
                  placeholder="https://cdn.jsdelivr.net/npm/chart.js@4.4.9/dist/chart.umd.js"
                  maxLength="500"
                  style={{
                    width: "100%",
                    border: validationErrors.source_url
                      ? "2px solid #ff4444"
                      : "1px solid #ddd",
                  }}
                />
                {validationErrors.source_url && (
                  <div
                    style={{
                      color: "#ff4444",
                      fontSize: "12px",
                      marginTop: "5px",
                    }}
                  >
                    {validationErrors.source_url}
                  </div>
                )}
              </dd>
            </dl>

            {/* 설명 및 차단 사유 - 전체 너비 */}
            <dl>
              <dt>정책 설명</dt>
              <dd>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleFormChange}
                  rows="3"
                  placeholder="정책에 대한 설명을 입력하세요"
                  maxLength="1000"
                  style={{ width: "100%", height: "60px", resize: "vertical" }}
                />
              </dd>
            </dl>

            <dl>
              <dt>차단 사유</dt>
              <dd>
                <textarea
                  name="blocking_reason"
                  value={formData.blocking_reason}
                  onChange={handleFormChange}
                  rows="3"
                  placeholder={
                    formData.is_allowed
                      ? "허용된 라이브러리는 차단 사유가 필요하지 않습니다."
                      : "차단 사유를 구체적으로 입력하세요 (예: 보안 취약점 CVE-2023-1234가 발견된 버전입니다.)"
                  }
                  disabled={formData.is_allowed}
                  maxLength="1000"
                  style={{
                    width: "100%",
                    height: "70px",
                    resize: "vertical",
                    backgroundColor: formData.is_allowed ? "#f5f5f5" : "white",
                    color: formData.is_allowed ? "#999" : "black",
                  }}
                />
                <small
                  style={{
                    color: "#666",
                    fontSize: "0.85em",
                    display: "block",
                    marginTop: "5px",
                  }}
                >
                  {formData.is_allowed
                    ? "허용 상태를 '차단'으로 변경하면 차단 사유를 입력할 수 있습니다."
                    : "사용자에게 표시될 구체적인 차단 이유를 입력하세요."}
                </small>
              </dd>
            </dl>

            <dl style={{ marginBottom: "10px" }}>
              <dt>정책 규칙 (JSON)</dt>
              <dd>
                <textarea
                  name="policy_rules"
                  value={formData.policy_rules}
                  onChange={handleFormChange}
                  rows="4"
                  placeholder='{"max_data_points": 1000, "allowed_chart_types": ["scatter", "line", "bar"]}'
                  maxLength="5000"
                  style={{
                    width: "100%",
                    height: "90px",
                    resize: "vertical",
                    fontFamily: "monospace",
                  }}
                />
              </dd>
            </dl>
          </div>
        </Modal>
      </SectionContainer>
    );
};

export default MobileCodePolicyPage;
